import { useCallback, useState } from "react";
import apiRequest from "../../../utils/ApiRequest/apiRequest.js";
import Cookies from "universal-cookie";
import config from "config";
import ToastHtmlComponent from "../../Reusable/ToastHtmlComponent/ToastHtmlComponent.js";
import FormValidationMessage from "components/Reusable/Form/FormValidationMessage.js";
import MicrosoftLoginButton from "./components/MicrosoftLoginButton.tsx";
import GoogleLoginButton from "./components/GoogleLoginButton.tsx";
import useCustomLoginErrorHandler from "hooks/useCustomLoginErrorHandler.js";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { useToasts } from "react-toast-notifications";
import { useModal } from "../../../contexts/modalContext/modalContext.js";
import { useUser } from "../../../contexts/userContext/userContext.js";
import { setLocalStorage } from "utils/globalLocalStorage.js";

import "./SocialMediaAuthentication.scss";
import addRefererToBodyRequest from "helpers/addRefererToBodyRequest.ts";
import { GoogleOAuthProvider } from "@react-oauth/google";

const cookies = new Cookies();

export const SocialMediaAuthentication = ({
  callback = null,
  completeAddSubUser,
  addSubUserToOrganization,
  AuthenticationOptions,
  inviteDomainName,
}) => {
  const {
    getBasicSettingsData,
    getTextByKey,
    getPath,
    getCurrentPage,
    getLanguage,
  } = useContent();
  const { addToast } = useToasts();
  const { closeModal } = useModal();
  const { setUser } = useUser();
  const [errorResponse, setErrorResponse] = useState(null);

  const handleCustomError = useCustomLoginErrorHandler();

  const handleApiAuth = useCallback(
    async (body) => {
      addRefererToBodyRequest(body);

      const response = await apiRequest(
        "Users",
        "APIExternalLogin",
        body,
        "POST",
        null,
        "application/json",
        null,
        false,
        true
      );
      if (response.status === "success") {
        const userName = response.data.UserData.UserName;
        const hasSameOrg =
          response.data.UserData.UserName.includes(inviteDomainName);
        const isEmailVerified = response.data.UserData.IsConfirm;
        const isSubscribed = response.data.UserData.IsSubscribed;
        const isSubUser = response.data.UserData.IsSubUser;
        const isInvitePage = getCurrentPage() === "invite";

        // Set auth token to cookies
        document.cookie =
          config.api.jumbomailApiAuthCookie +
          "=" +
          response.data?.BearerToken +
          ";path=/";

        // Implementation of the logic according to
        // https://docs.google.com/spreadsheets/d/1d1_xT5uO2RsRdr3PLHgakaCSG8ageNN6UHqaBL_jPGM/edit#gid=0
        //
        // for Sign In with the invitation link.

        if (isInvitePage) {
          // Cases 1, 2 and 3.
          // If domain coincides with the org domain
          // and user isn't subscriber yet and email is verified.
          // In other words - FULL SUCCESS
          if (hasSameOrg && !isSubscribed && isEmailVerified) {
            await addSubUserToOrganization?.(userName);
          }

          // Cases 4 and 5.
          // The same as above but email isn't verified.
          // SUCCESS, but sub user status will be PENDING.
          else if (hasSameOrg && !isSubscribed && !isEmailVerified) {
            await addSubUserToOrganization?.(userName);
            window.location.href =
              config.baseurl + `/${getLanguage()}/registration-complete`;
          }

          // Cases 6, 7 and 8.
          // ACCESS DENIED because of not correct domain.

          // Handles in the InvitationLinkCard.js
          // else if (!hasSameOrg) {}

          // Cases 9 and 10.
          // If already is subscribed

          // Handles in the InvitationLinkCard.js
          // else if (hasSameOrg && isEmailVerified && isSubscribed) {}

          // Case 11.
          // If already is sub user and email is verified.
          else if (isSubUser && isEmailVerified) {
            window.location.href = config.baseurl + `/${getLanguage()}/inbox`;
          }

          // Case 12.
          // If already is sub user but email isn't verified.
          else if (isSubUser && !isEmailVerified) {
            window.location.href =
              config.baseurl + `/${getLanguage()}/registration-complete`;
          }
        }

        cookies.set("loginDate", response.data.nowDate, {
          path: "/",
          domain: ".jumbomail.me",
        });

        if (callback !== null) {
          setLocalStorage("proccessSignUpEmbedPage", true);
        }
        setUser(response.data);
        if (callback !== null) {
          if (response.data) {
            let tempMessage = {
              UserData: response.data.UserData,
              IsRegistered: true,
            };
            callback(tempMessage);
          }
        } else if (response.data.IsRegistered) {
          window.location.href = config.baseurl + getPath();
        }
        addToast(getTextByKey("sign-in-success"), { appearance: "success" });

        if (
          getCurrentPage() === "registration" &&
          !response.data.IsRegistered
        ) {
          window.location.href = config.baseurl + getPath();
        }
        closeModal();

        if (completeAddSubUser) {
          // update sub user data in the top user sub user list: make user active and verified
          await completeAddSubUser(userName);
        }

        return {
          callBack: closeModal,
          status: "success",
        };
      } else {
        if (
          response?.data?.includes("microsoft") ||
          response?.data?.includes("google") ||
          response?.data?.includes("sub-user-must-join-organization")
        ) {
          handleCustomError(response.data);
        } else {
          let errText = getTextByKey("api-error");
          errText = errText.replace("#ERRORNUM#", 1);
          errText = errText.replace("#SYSMAIL#", "contact-us");
          errText = errText.replace("#SYSMAIL#", config.supportmail);
          errText = errText.replace("#SYSMAIL#", "contact-us");
          addToast(<ToastHtmlComponent htmlStr={errText} />, {
            appearance: "error",
          });
          setErrorResponse([
            {
              message: response.data,
              status: response.status,
            },
          ]);
        }

        return {
          message: response.message,
          status: response.status,
        };
      }
    },
    [
      addSubUserToOrganization,
      callback,
      completeAddSubUser,
      getCurrentPage,
      getLanguage(),
      getPath(),
      inviteDomainName,
    ]
  );
  return getBasicSettingsData().googleClientId &&
    getBasicSettingsData().microsoftClientId ? (
    <GoogleOAuthProvider clientId={getBasicSettingsData().googleClientId}>
      <>
        <div className="buttons-container">
          {(AuthenticationOptions
            ? AuthenticationOptions?.GoogleSSO
            : true) && <GoogleLoginButton handleApiAuth={handleApiAuth} />}
          {(AuthenticationOptions
            ? AuthenticationOptions?.MicrosoftSSO
            : true) && <MicrosoftLoginButton handleApiAuth={handleApiAuth} />}
        </div>
        {errorResponse && (
          <div className="external-login-error-container">
            <FormValidationMessage errorMessageObj={errorResponse} />
          </div>
        )}
      </>
    </GoogleOAuthProvider>
  ) : null;
};
export default SocialMediaAuthentication;
